import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, ChevronRight, Cpu, Shield, Zap, Home, Sparkles } from "lucide-react";
import { PlatformShellHeader, PlatformShellFooter, useSeo } from "./PlatformShell";
import { getFamily, FAMILIES } from "../data/catalog";
import { SkuCarousel } from "./SkuCarousel";

/* /products — overview page listing all 4 families */
export const ProductsOverview = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  useSeo({
    title: "BBC Product Portfolio · 4 Families · 13 SKUs",
    description:
      "The complete BBC boom-barrier portfolio — PRIME 120W, ELITE 150W, FLASH 180W and MAX 8–12m. Every SKU with cabinet, motor, arm and use-case specs.",
    canonical: "https://boombarriercompany.com/products",
  });

  return (
    <div className="bg-[#0F1113] min-h-screen text-white">
      <PlatformShellHeader />
      <main className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24" data-testid="products-overview">
        <nav className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A] flex items-center gap-2 mb-8" data-testid="products-breadcrumb">
          <Link to="/" className="hover:text-white flex items-center gap-1"><Home size={11} /> Home</Link>
          <span>/</span>
          <span className="text-white">Products</span>
        </nav>

        <div className="grid lg:grid-cols-12 gap-8 items-end">
          <h1 className="lg:col-span-8 font-display text-4xl md:text-6xl lg:text-7xl font-black tracking-[-0.02em] leading-[1.02]" data-testid="products-heading">
            The BBC
            <br />
            <span className="text-[#A1A1AA]">Portfolio.</span>
          </h1>
          <div className="lg:col-span-4">
            <p className="text-[#A1A1AA] text-base md:text-lg font-body">
              Four families. Thirteen SKUs. Same BBC Trust platform inside every barrier — verified, tracked and remotely operable.
            </p>
            <Link
              to="/products/compare"
              data-testid="products-compare-cta"
              className="mt-5 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-[#00FF85] border border-[#00FF85]/40 hover:border-[#00FF85] hover:bg-[#00FF85]/10 px-4 py-2.5 transition"
            >
              Compare all families
              <ArrowRight size={12} />
            </Link>
          </div>
        </div>

        <div className="mt-16 grid md:grid-cols-2 gap-4">
          {FAMILIES.map((f, i) => (
            <Link
              to={`/products/${f.slug}`}
              key={f.slug}
              data-testid={`products-family-${f.slug}`}
              className="group relative border border-white/10 bg-[#16191C] hover:border-[#00FF85]/50 transition-all overflow-hidden"
            >
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#00FF85]/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity z-10" />

              {/* Product image carousel — same 'hero' variant as family detail page */}
              <SkuCarousel
                skus={f.skus}
                accent={f.accentColor}
                variant="hero"
                disableLightbox
                intervalMs={4200 + i * 300}
                testId={`products-family-${f.slug}-carousel`}
              />

              <div className="p-7 md:p-9">
                <div className="flex items-baseline gap-3 flex-wrap">
                  <h2 className="font-display text-white text-4xl md:text-5xl font-black tracking-tight">{f.name}</h2>
                  <span className="font-mono text-[11px] uppercase tracking-[0.2em]" style={{ color: f.accentColor }}>{f.wattage}</span>
                </div>
                <p className="mt-3 text-[#00FF85] font-mono text-[11px] uppercase tracking-[0.22em]">{f.tagline}</p>
                <p className="mt-5 text-[#A1A1AA] text-sm md:text-base font-body leading-relaxed">{f.positioning}</p>

                <div className="mt-5 flex flex-wrap gap-2">
                  {f.skus.map((s) => (
                    <span key={s.code} className="font-mono text-[10px] uppercase tracking-wider text-white/70 border border-white/10 px-2 py-1 bg-white/[0.02]">
                      {s.code}
                    </span>
                  ))}
                </div>

                <div className="mt-7 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-white group-hover:text-[#00FF85] transition">
                  Explore family <ArrowRight size={12} />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </main>
      <PlatformShellFooter />
    </div>
  );
};

/* /products/:family — family detail page listing its SKUs */
export const ProductFamily = () => {
  const { family: familySlug } = useParams();
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const family = getFamily(familySlug);

  useSeo({
    title: family
      ? `BBC ${family.name} ${family.wattage} · ${family.skus.length} SKU${family.skus.length > 1 ? "s" : ""} · Boom Barrier Company`
      : "BBC Product Family",
    description: family
      ? `${family.name} ${family.wattage} — ${family.tagline.replace(/\.$/, "")}. ${family.positioning}`
      : "",
    canonical: family ? `https://boombarriercompany.com/products/${family.slug}` : undefined,
  });

  if (!family) return <Navigate to="/products" replace />;

  return (
    <div className="bg-[#0F1113] min-h-screen text-white">
      <PlatformShellHeader />
      <main className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24" data-testid={`family-${family.slug}`}>
        <nav className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A] flex items-center gap-2 mb-8" data-testid="family-breadcrumb">
          <Link to="/" className="hover:text-white flex items-center gap-1"><Home size={11} /> Home</Link>
          <span>/</span>
          <Link to="/products" className="hover:text-white">Products</Link>
          <span>/</span>
          <span className="text-white">{family.name}</span>
        </nav>

        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: family.accentColor }}>
              BBC · {family.wattage}
            </div>
            <h1 className="mt-3 font-display text-white text-5xl md:text-6xl lg:text-7xl font-black tracking-[-0.02em] leading-[0.98]">
              {family.name}
            </h1>
            <p className="mt-5 font-mono text-[12px] uppercase tracking-[0.22em] text-[#00FF85]">
              {family.tagline}
            </p>
            <p className="mt-6 text-[#A1A1AA] text-base md:text-lg font-body leading-relaxed max-w-2xl">
              {family.positioning}
            </p>

            <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-2">
              <SpecTile icon={Cpu} label="Motor Class" value={family.motorClass} />
              <SpecTile icon={Zap} label="Cycle Time" value={family.speedRange} />
              <SpecTile icon={Shield} label="Arm Range" value={family.armRange} />
            </div>

            {/* OEM / custom-branding note — applies across every drive tier */}
            <div
              data-testid={`family-${family.slug}-oem-note`}
              className="mt-6 border-l-2 border-[#00FF85] bg-[#00FF85]/[0.05] px-4 py-3 flex items-start gap-3 max-w-2xl"
            >
              <Sparkles size={14} className="text-[#00FF85] mt-0.5 shrink-0" />
              <p className="text-white/85 text-sm font-body leading-relaxed">
                <span className="text-white font-semibold">OEM & custom-branded builds</span> available on every {family.name} SKU —
                custom cabinet colours, graphics, LED signage and white-label options.{" "}
                <a
                  href="/#videos"
                  className="text-[#00FF85] hover:underline font-medium"
                  data-testid={`family-${family.slug}-oem-link`}
                >
                  See examples ↓
                </a>
              </p>
            </div>
          </div>

          <div className="lg:col-span-5">
            <SkuCarousel
              skus={family.skus}
              accent={family.accentColor}
              testId={`family-${family.slug}-carousel`}
            />
          </div>
        </div>

        {/* Use cases */}
        <div className="mt-20 border-t border-white/5 pt-12">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">Designed for</div>
          <div className="mt-4 grid md:grid-cols-2 lg:grid-cols-4 gap-3">
            {family.useCases.map((u) => (
              <div key={u} className="border border-white/10 bg-white/[0.02] px-4 py-3 text-white/85 text-sm font-body">
                {u}
              </div>
            ))}
          </div>
        </div>

        {/* SKU grid */}
        <div className="mt-20 border-t border-white/5 pt-12">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">Available SKUs</div>
              <h2 className="mt-2 font-display text-white text-3xl md:text-4xl font-black tracking-tight">
                {family.name} Lineup
              </h2>
            </div>
            <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#A1A1AA]">
              {family.skus.length} configuration{family.skus.length > 1 ? "s" : ""}
            </div>
          </div>

          <div className="mt-8 grid md:grid-cols-2 gap-3">
            {family.skus.map((s, i) => (
              <Link
                to={`/products/${family.slug}/${s.code.toLowerCase()}`}
                key={s.code}
                data-testid={`family-sku-${s.code.toLowerCase()}`}
                className="group relative border border-white/10 bg-[#16191C] hover:border-[#00FF85]/50 transition overflow-hidden"
              >
                <div className="relative aspect-[16/9] bg-gradient-to-br from-[#1F2326] via-[#16191C] to-black overflow-hidden">
                  <img
                    src={s.image}
                    alt={`${s.name} — ${s.tagline}`}
                    className="absolute inset-0 w-full h-full object-contain p-5 group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute top-3 left-4 right-4 flex items-center justify-between">
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/70">SKU.0{i + 1}</span>
                    <span className="font-mono text-[9px] uppercase tracking-wider text-[#00FF85] bg-black/40 backdrop-blur px-2 py-1 border border-white/10">
                      <span className="inline-block w-1 h-1 rounded-full bg-[#00FF85] mr-1.5 align-middle" />
                      In stock
                    </span>
                  </div>
                </div>
                <div className="p-6 md:p-7">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-display text-white text-2xl md:text-3xl font-black tracking-tight">{s.code}</h3>
                      <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.22em]" style={{ color: family.accentColor }}>
                        {s.tagline}
                      </p>
                    </div>
                    <ChevronRight size={16} className="text-white/40 group-hover:text-[#00FF85] transition mt-2" />
                  </div>
                  <p className="mt-4 text-[#A1A1AA] text-sm font-body">{s.cabinet}</p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {s.highlights.slice(0, 3).map((h) => (
                      <span key={h} className="font-mono text-[9px] uppercase tracking-wider text-white/70 border border-white/10 px-2 py-1">
                        {h}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        <div className="mt-16 border-t border-white/5 pt-10 flex flex-wrap items-center justify-between gap-4">
          <Link to="/products" className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#A1A1AA] hover:text-white" data-testid="family-back">
            ← All families
          </Link>
          <div className="flex items-center gap-3 flex-wrap">
            <Link
              to="/products/compare"
              data-testid={`family-compare-${family.slug}`}
              className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-[#00FF85] border border-[#00FF85]/40 hover:border-[#00FF85] hover:bg-[#00FF85]/10 px-4 py-3 transition"
            >
              Compare all families
              <ArrowRight size={12} />
            </Link>
            <Link
              to={`/quote?model=${encodeURIComponent(family.name)}`}
              data-testid={`family-cta-${family.slug}`}
              className="inline-flex items-center gap-2 bg-[#00FF85] hover:bg-[#00CC6A] text-black font-semibold px-5 py-3 text-xs uppercase tracking-wider transition"
            >
              Get quote for {family.name}
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </main>
      <PlatformShellFooter />
    </div>
  );
};

const SpecTile = ({ icon: Icon, label, value }) => (
  <div className="border border-white/10 bg-white/[0.02] px-3 py-3">
    <div className="flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.22em] text-[#52525B]">
      <Icon size={11} />
      {label}
    </div>
    <div className="mt-1 font-display text-white text-sm font-bold leading-tight">{value}</div>
  </div>
);
